import { useCallback, useState } from "react"

import { useQuery } from "@tanstack/react-query"
import toast from "react-hot-toast"
import { Button, Divider, Icon, SegmentGroup } from "semantic-ui-react"
import type { OpenAPI } from "simplydo/interfaces"
import { useThrottle } from "simplydo/react/hooks"

import api, { asQuery } from "api"
import util from "utils/utils"

import { ImpactStatementEditor } from "../../ImpactStatements/ImpactStatementEditor"
import { StageSegment } from "../../ImpactStatements/StageSegments"

type ImpactStatements = OpenAPI.GET<"/ideas/{idea_id}/impactStatement">["response"]["impactStatements"]

type ImpactStatementManagementProps = {
  idea: OpenAPI.GET<"/ideas/{id}">["response"]
  onUpdateIdea: (data: any) => void
  impactList: any[]
}

const ImpactStatementManagement = ({ idea, onUpdateIdea, impactList }: ImpactStatementManagementProps) => {
  const [currentImpactStage, setCurrentImpactStage] = useState<ImpactStatements["userEstimate"]["stage"] | null>(null)

  const impactStatementQuery = useQuery({
    queryKey: ["ideas", idea._id, "impactStatement"],
    queryFn: asQuery(api.ideas.getImpactStatements, {
      params: [idea._id, null],
    })!,
  })

  const throttledUpdate = useThrottle(onUpdateIdea, 400, [onUpdateIdea])
  const updateImpactStatements = useCallback(
    (impactStatements: ImpactStatements) => {
      api.queryClient.setQueryData(["ideas", idea._id, "impactStatement"], { impactStatements })

      const mostImportantStatement =
        impactStatements.finalOutcome || impactStatements.projectEstimate || impactStatements.userEstimate
      throttledUpdate({
        totalImpacts: Object.entries(mostImportantStatement.impacts)
          .filter(([_key, value]) => value.enabled)
          .map(([key, value]) => {
            const impactOptions = idea?.ownerChallenge?.impactStatements?.impacts?.[key]?.options || []

            let maxWeight = 1
            for (const option of impactOptions) {
              if (option.weight == null) continue
              if (option.weight > maxWeight) {
                maxWeight = option.weight
              }
            }

            return {
              impactId: key,
              ownerImpact: impactList.find((impact) => impact._id === key),
              ...value,
              maxWeight,
            }
          }),
      })
    },
    [throttledUpdate, impactList, idea._id, idea?.ownerChallenge?.impactStatements],
  )

  const deleteStatement = (impactStatementId: string) => {
    api.ideas.deleteImpactStatement(
      idea._id,
      impactStatementId,
      () => {
        toast.success("Statement deleted successfully")
        api.queryClient.setQueryData(
          ["ideas", idea._id, "impactStatement"],
          (prev: { impactStatements: ImpactStatements }) => {
            const newStatements = { ...(prev.impactStatements || {}) }
            const deletedStatement = Object.entries(newStatements).find(
              ([_, statement]) => statement._id === impactStatementId,
            )
            if (deletedStatement) {
              delete newStatements[deletedStatement[0]]
            }
            return { impactStatements: newStatements }
          },
        )
      },
      () => {
        toast.error("Failed to delete statement")
      },
    )
  }

  if (currentImpactStage) {
    return (
      <div>
        <Button basic compact onClick={() => setCurrentImpactStage(null)}>
          <Icon name="arrow left" />
          Back to overview
        </Button>

        <Divider />

        <ImpactStatementEditor
          impactStatements={impactStatementQuery.data?.impactStatements || {}}
          forStage={currentImpactStage}
          setImpactStatements={updateImpactStatements}
          statementDefintion={idea.ownerChallenge.impactStatements}
          ideaId={idea._id}
          restrictStagesTo={[currentImpactStage]}
          editable={currentImpactStage !== "userEstimate"}
        />

        <Divider hidden />
        <Button
          secondary
          onClick={() => {
            setCurrentImpactStage(null)
          }}
        >
          <Icon name="check" />
          Done
        </Button>
      </div>
    )
  }

  return (
    <div>
      <SegmentGroup>
        {idea.ownerChallenge.impactStatements.showOnIdea ? (
          <StageSegment
            stage="userEstimate"
            impactStatement={impactStatementQuery.data?.impactStatements?.userEstimate}
            onView={() => {
              setCurrentImpactStage("userEstimate")
            }}
          />
        ) : null}

        <StageSegment
          stage="projectEstimate"
          impactStatement={impactStatementQuery.data?.impactStatements?.projectEstimate}
          onView={() => {
            setCurrentImpactStage("projectEstimate")
          }}
          onCreate={() => {
            setCurrentImpactStage("projectEstimate")
          }}
          onDelete={() => {
            util
              .confirm(
                "Delete the project estimate?",
                "Are you sure you want to delete the project estimate statement? This action is instant and irreversible.",
              )
              .then(() => {
                deleteStatement(impactStatementQuery.data?.impactStatements?.projectEstimate?._id)
              })
              .catch(() => {})
          }}
        />
        <StageSegment
          stage="finalOutcome"
          impactStatement={impactStatementQuery.data?.impactStatements?.finalOutcome}
          onView={() => {
            setCurrentImpactStage("finalOutcome")
          }}
          onCreate={() => {
            setCurrentImpactStage("finalOutcome")
          }}
          onDelete={() => {
            util
              .confirm(
                "Delete final outcome?",
                "Are you sure you want to delete the final outcome statement? This action is instant and irreversible.",
              )
              .then(() => {
                deleteStatement(impactStatementQuery.data?.impactStatements?.finalOutcome?._id)
              })
              .catch(() => {})
          }}
        />
      </SegmentGroup>
    </div>
  )
}

export default ImpactStatementManagement
